import { BarChart } from '../../../components/BarChart.js';
import { useTeamsInfo } from '../../common/admin-hooks.js';
import { useScenariosList } from '../../scenario/hooks.js';

/**
 * Planning › Visualization. Two simple charts derived client-side
 * from the scenario + team feeds. Phase B replaces these with
 * timeseries / drill-down charts backed by a real charting library.
 */
export function PlanningVisualizationPage(): JSX.Element {
  const { data: scenarios, isLoading: scenariosLoading } = useScenariosList();
  const { data: teams } = useTeamsInfo();

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Planning · Visualization</h1>
        <p className="text-sm text-foreground/60">
          License-level scenario distribution. Phase B layers timeseries + drill-down.
        </p>
      </header>
      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <h2 className="mb-2 text-sm font-medium text-foreground/80">Scenarios per team</h2>
          {scenariosLoading ? (
            <div className="p-6 text-center text-xs text-foreground/40">Loading…</div>
          ) : (
            <BarChart data={byTeam(scenarios ?? [], teams ?? [])} />
          )}
        </section>
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <h2 className="mb-2 text-sm font-medium text-foreground/80">
            Scenarios per organization
          </h2>
          {scenariosLoading ? (
            <div className="p-6 text-center text-xs text-foreground/40">Loading…</div>
          ) : (
            <BarChart data={byOrg(scenarios ?? [])} />
          )}
        </section>
      </div>
    </div>
  );
}

function byTeam(
  scenarios: ReadonlyArray<Record<string, unknown>>,
  teams: ReadonlyArray<Record<string, unknown>>,
): { label: string; value: number }[] {
  const teamNameById = new Map<string, string>();
  for (const t of teams) {
    const id = (t.id as string | number | undefined) ?? (t.teamId as string | number | undefined);
    const name = (t.name as string | undefined) ?? (t.teamName as string | undefined);
    if (id !== undefined && name) teamNameById.set(String(id), name);
  }
  const counts = new Map<string, number>();
  for (const s of scenarios) {
    const tid =
      (s.teamId as string | number | undefined) ??
      (s.team_id as string | number | undefined) ??
      (s.fusionTeamId as string | number | undefined);
    const label =
      (s.teamName as string | undefined) ??
      (tid !== undefined ? (teamNameById.get(String(tid)) ?? `Team ${tid}`) : 'Unassigned');
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

function byOrg(
  scenarios: ReadonlyArray<Record<string, unknown>>,
): { label: string; value: number }[] {
  const counts = new Map<string, number>();
  for (const s of scenarios) {
    const label =
      (s.orgName as string | undefined) ??
      (s.organizationName as string | undefined) ??
      (s.fusionOrgName as string | undefined) ??
      'Unassigned';
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}
